// ---------------------------------------------------------------------------
// ตะกร้า (Cart) — รวมแพ็กเกจหลัก / แพ็ค point / เติม point เอง / ฟีเจอร์เสริม / โควตาเสริม จ่าย QR ครั้งเดียว
// ---------------------------------------------------------------------------
// ไฟล์นี้เป็น logic ล้วน (ไม่แตะ React/service) เพื่อเทสต์ได้
//   - ในตะกร้าเก็บแค่ "ข้อมูลพอคำนวณใหม่" ห้ามเก็บราคา — ราคาคำนวณใหม่ทุกครั้งที่แสดง/จ่าย
//   - ลำดับ apply หลังจ่าย: แพ็กเกจหลัก -> ฟีเจอร์เสริม -> point -> ฟีเจอร์ custom (ฟีเจอร์เสริมผูกกับวันหมดอายุที่ได้หลังต่อแพ็กเกจ)
// ---------------------------------------------------------------------------
import {
  POINT_PACK_MAP,
  QUOTA_ADDON_MAP,
  MODULE_ADDON_PRICES,
  CUSTOM_TOPUP,
  pointsForAmount,
  proratePrice,
} from "./addons";
import { FEATURE_MODULE_MAP } from "./feature-modules";
import { quotePayable, INVALID_QUOTE_MESSAGE, INVALID_QUOTE_MESSAGE_EN } from "./custom-request";
import { getPlan, priceOf, monthsOf, nextExpireAt, resolveSubscription, clampSeats, CYCLE_MAP } from "./plans";

export const CART_MAX_QTY = 99;
export const CART_MAX_BLOCKS = 20;

export const cartStorageKey = (orgId) => `lucid:cart:${orgId}`;

const clamp = (value, min, max) => Math.min(max, Math.max(min, Math.floor(Number(value)) || min));

// ----------------------------- id ของรายการ -----------------------------

/** seats = จำนวน User (แพ็กเกจต่อ User) ปัดเข้าช่วงของแพ็กเกจ */
export const planItem = (planKey, cycle = "monthly", seats) => ({ id: "plan", kind: "plan", planKey, cycle, seats: clampSeats(planKey, seats) });

export const pointsItem = ({ packKey, amount, qty = 1 }) =>
  packKey
    ? { id: `points:${packKey}`, kind: "points", packKey, qty: clamp(qty, 1, CART_MAX_QTY) }
    : { id: `points:custom:${Math.floor(Number(amount) || 0)}`, kind: "points", amount: Math.floor(Number(amount) || 0), qty: clamp(qty, 1, CART_MAX_QTY) };

/** addonKind = module | quota · ซื้อโมดูลซ้ำ/ต่ออายุรายการเดิมซ้ำใช้ id เดียวกัน = ใส่ซ้ำไม่ได้ */
export const addonItem = ({ addonKind, key, blocks = 1, renewId = "" }) => {
  const id =
    addonKind === "module"
      ? `addon:module:${key}`
      : renewId
      ? `addon:quota:${key}:renew:${renewId}`
      : `addon:quota:${key}:new`;
  return {
    id,
    kind: "addon",
    addonKind,
    key,
    blocks: addonKind === "quota" ? clamp(blocks, 1, CART_MAX_BLOCKS) : 1,
    renewId: renewId || "",
  };
};

/** ฟีเจอร์ custom ตามใบเสนอราคา — ในตะกร้าเก็บแค่ id คำขอ ราคาอ่านจากใบเสนอราคาทุกครั้งที่แสดง/จ่าย */
export const customItem = (requestId) => ({ id: `custom:${requestId}`, kind: "custom", requestId: String(requestId) });

// ----------------------------- จัดการรายการ -----------------------------

/**
 * ใส่รายการเข้าตะกร้า — คืน { items, status }
 *   status: added | replaced (แพ็กเกจหลักถูกแทนที่) | merged (เพิ่มจำนวน) | duplicate (ซ้ำ ไม่เพิ่ม)
 */
export const addToCart = (items, item) => {
  const list = Array.isArray(items) ? items : [];
  const existing = list.find((row) => row.id === item.id);

  if (item.kind === "plan") {
    const others = list.filter((row) => row.kind !== "plan");
    return { items: [item, ...others], status: existing || list.some((row) => row.kind === "plan") ? "replaced" : "added" };
  }

  if (!existing) return { items: [...list, item], status: "added" };

  if (item.kind === "points") {
    const qty = clamp(existing.qty + item.qty, 1, CART_MAX_QTY);
    return { items: list.map((row) => (row.id === item.id ? { ...row, qty } : row)), status: "merged" };
  }

  // โควตาซื้อใหม่ของตัวเดิม = เพิ่มจำนวนก้อน · โมดูล/ต่ออายุ = ซ้ำไม่ได้
  if (item.kind === "addon" && item.addonKind === "quota" && !item.renewId) {
    const blocks = clamp(existing.blocks + item.blocks, 1, CART_MAX_BLOCKS);
    return { items: list.map((row) => (row.id === item.id ? { ...row, blocks } : row)), status: "merged" };
  }

  return { items: list, status: "duplicate" };
};

export const removeFromCart = (items, id) => (items || []).filter((row) => row.id !== id);

/** ปรับจำนวน: point = qty · โควตาซื้อใหม่ = blocks */
export const setCartQty = (items, id, value) =>
  (items || []).map((row) => {
    if (row.id !== id) return row;
    if (row.kind === "points") return { ...row, qty: clamp(value, 1, CART_MAX_QTY) };
    if (row.kind === "addon" && row.addonKind === "quota" && !row.renewId) return { ...row, blocks: clamp(value, 1, CART_MAX_BLOCKS) };
    return row;
  });

export const cartCount = (items) =>
  (items || []).reduce((total, row) => total + (row.kind === "points" ? Number(row.qty) || 1 : 1), 0);

/** อ่านจาก localStorage แล้วกรองของเสีย/ของที่ไม่มีในระบบแล้วทิ้ง (ไม่ throw) */
export const sanitizeCart = (raw) => {
  if (!Array.isArray(raw)) return [];
  let result = [];
  raw.forEach((row) => {
    if (!row || typeof row !== "object") return;
    let item = null;
    if (row.kind === "plan" && getPlan(row.planKey) && !getPlan(row.planKey).quoteOnly) {
      item = planItem(getPlan(row.planKey).key, CYCLE_MAP[row.cycle] ? row.cycle : "monthly", row.seats);
    } else if (row.kind === "points") {
      if (row.packKey && POINT_PACK_MAP[row.packKey]) item = pointsItem({ packKey: row.packKey, qty: row.qty });
      else if (!row.packKey && pointsForAmount(row.amount)) item = pointsItem({ amount: row.amount, qty: row.qty });
    } else if (row.kind === "custom") {
      if (row.requestId && typeof row.requestId === "string") item = customItem(row.requestId);
    } else if (row.kind === "addon") {
      const known = row.addonKind === "module" ? MODULE_ADDON_PRICES[row.key] : row.addonKind === "quota" ? QUOTA_ADDON_MAP[row.key] : null;
      if (known) item = addonItem({ addonKind: row.addonKind, key: row.key, blocks: row.blocks, renewId: String(row.renewId || "") });
    }
    if (item) result = addToCart(result, item).items;
  });
  return result;
};

// ----------------------------- คำนวณราคา -----------------------------

const latestOwned = (organization, kind, key) =>
  (Array.isArray(organization?.addons) ? organization.addons : [])
    .filter((row) => row && row.kind === kind && row.key === key)
    .sort((a, b) => new Date(b.expireAt) - new Date(a.expireAt))[0] || null;

/**
 * คำนวณทุกบรรทัดของตะกร้าใหม่จากสถานะร้านปัจจุบัน
 * line.status: ok | unneeded (จ่ายได้แต่ไม่จำเป็น) | blocked (ไม่มีแพ็กเกจรองรับ) | invalid (ไม่พบรายการ)
 */
export const priceCart = (items, organization, context = {}) => {
  const customRequests = Array.isArray(context?.customRequests) ? context.customRequests : [];
  const list = Array.isArray(items) ? items : [];
  const sub = resolveSubscription(organization);
  const plan = list.find((row) => row.kind === "plan");
  const planDef = plan ? getPlan(plan.planKey) : null;

  // วันหมดอายุแพ็กเกจหลัก "หลังจ่ายตะกร้านี้" — ถ้าไม่มีแพ็กเกจในตะกร้าและแพ็กเกจเดิมไม่ active = ไม่มี
  const expireAfter = planDef ? nextExpireAt(organization?.planExpireAt, plan.cycle) : sub.isActive ? sub.expireAt : "";
  const effectivePlan = planDef || (sub.isActive ? sub.plan : null);

  const lines = list.map((item) => {
    if (item.kind === "plan") {
      const def = getPlan(item.planKey);
      if (!def || def.quoteOnly) return invalidLine(item);
      const cycleName = CYCLE_MAP[item.cycle];
      const seats = clampSeats(item.planKey, item.seats);
      const amount = priceOf(item.planKey, item.cycle, seats);
      return {
        id: item.id, kind: "plan", item, qty: 1, unit: amount, amount, days: 0, status: "ok", note: "", seats,
        name: `${def.name}${def.perUser ? ` ${seats} User` : ""} (${monthsOf(item.cycle)} เดือน)`,
        nameEn: `${def.nameEn}${def.perUser ? ` ${seats} users` : ""} (${monthsOf(item.cycle)} months)`,
        detail: cycleName ? cycleName.name : "", detailEn: cycleName ? cycleName.nameEn : "",
        expireAt: expireAfter,
      };
    }

    if (item.kind === "points") {
      const pack = item.packKey ? POINT_PACK_MAP[item.packKey] : null;
      const unit = pack ? pack.price : Math.floor(Number(item.amount) || 0);
      const points = pack ? pack.points : pointsForAmount(unit);
      if ((item.packKey && !pack) || !points || (!pack && (unit < CUSTOM_TOPUP.min || unit > CUSTOM_TOPUP.max))) return invalidLine(item);
      const qty = clamp(item.qty, 1, CART_MAX_QTY);
      return {
        id: item.id, kind: "points", item, qty, unit, amount: unit * qty, days: 0, status: "ok", note: "",
        points, totalPoints: points * qty,
        name: pack ? pack.name : `เติม ${unit.toLocaleString()} บาท`,
        nameEn: pack ? pack.nameEn : `Top up ${unit.toLocaleString()} THB`,
      };
    }

    if (item.kind === "addon") return priceAddon(item, organization, expireAfter, effectivePlan);
    if (item.kind === "custom") return priceCustom(item, customRequests, expireAfter);
    return invalidLine(item);
  });

  const payable = lines.filter((row) => row.status === "ok" || row.status === "unneeded");
  return {
    lines,
    total: payable.reduce((sum, row) => sum + row.amount, 0),
    expireAfter,
    hasPlan: !!planDef,
    hasBlocked: lines.some((row) => row.status === "blocked"),
    hasInvalid: lines.some((row) => row.status === "invalid"),
    canPay: lines.length > 0 && lines.every((row) => row.status === "ok" || row.status === "unneeded"),
  };
};

const invalidLine = (item) => ({
  id: item.id, kind: item.kind, item, qty: 1, unit: 0, amount: 0, days: 0, status: "invalid", note: "invalid",
  name: "รายการไม่ถูกต้อง", nameEn: "Invalid item",
});

// ฟีเจอร์ custom: ราคา = amount ในใบเสนอราคาที่ผ่านการตรวจเท่านั้น (ไม่ผ่าน/สถานะไม่ใช่ quoted = จ่ายไม่ได้)
const priceCustom = (item, customRequests, expireAfter) => {
  const request = customRequests.find((row) => row && row.id === item.requestId);
  const base = {
    id: item.id, kind: "custom", item, qty: 1, unit: 0, amount: 0, days: 0, status: "ok", note: "", requestId: item.requestId,
    name: request?.serviceKey ? request.title : `ฟีเจอร์ custom: ${request?.title || item.requestId}`,
    nameEn: request?.serviceKey ? request.title : `Custom feature: ${request?.title || item.requestId}`,
  };
  if (!request) return { ...base, status: "invalid", note: "customMissing" };
  const check = quotePayable(request);
  if (!check.ok) {
    if (request.status !== "quoted") return { ...base, status: "invalid", note: "customNotPayable" };
    return { ...base, status: "invalid", note: "customInvalidQuote", message: INVALID_QUOTE_MESSAGE, messageEn: INVALID_QUOTE_MESSAGE_EN };
  }
  if (!expireAfter) return { ...base, status: "blocked", note: "needPlan" };
  return { ...base, unit: check.amount, amount: check.amount, mandays: check.mandays, rate: check.rate };
};

const priceAddon = (item, organization, expireAfter, effectivePlan) => {
  const isModule = item.addonKind === "module";
  const quota = isModule ? null : QUOTA_ADDON_MAP[item.key];
  const monthlyBase = isModule ? MODULE_ADDON_PRICES[item.key] : quota?.price;
  if (!monthlyBase) return invalidLine(item);

  const blocks = isModule ? 1 : clamp(item.blocks, 1, CART_MAX_BLOCKS);
  const owned = isModule
    ? latestOwned(organization, "module", item.key)
    : item.renewId
    ? (organization?.addons || []).find((row) => row && row.id === item.renewId) || null
    : null;
  const renewId = isModule ? owned?.id || "" : item.renewId || "";
  const action = renewId ? "renew" : "buy";

  const name = isModule
    ? `ฟีเจอร์เสริม ${FEATURE_MODULE_MAP[item.key]?.name || item.key}`
    : `${quota.name} +${(quota.block * blocks).toLocaleString()} ${quota.unit}`;
  const nameEn = isModule
    ? `Extra feature: ${FEATURE_MODULE_MAP[item.key]?.nameEn || item.key}`
    : `${quota.nameEn} +${(quota.block * blocks).toLocaleString()} ${quota.unitEn}`;

  const base = {
    id: item.id, kind: "addon", item, qty: 1, unit: 0, amount: 0, days: 0, status: "ok", note: "",
    addonKind: item.addonKind, key: item.key, blocks, renewId, action,
    name: `${action === "renew" ? "ต่ออายุ " : ""}${name}`,
    nameEn: `${action === "renew" ? "Renew " : ""}${nameEn}`,
  };

  if (!isModule && item.renewId && !owned) return { ...base, status: "invalid", note: "renewMissing" };
  if (!expireAfter) return { ...base, status: "blocked", note: "needPlan" };

  const start = owned && new Date(owned.expireAt) > new Date() ? owned.expireAt : undefined;
  const { days, amount } = proratePrice(monthlyBase * blocks, expireAfter, start);

  let note = "";
  if (isModule && effectivePlan?.modules.includes(item.key)) note = "inPlan";
  else if (!isModule && effectivePlan && effectivePlan.limits[item.key] === null && !(item.key === "staff" && effectivePlan.perUser)) note = "unlimited";
  else if (owned && new Date(owned.expireAt) >= new Date(expireAfter)) note = "covered";

  return { ...base, unit: amount, amount, days, status: note ? "unneeded" : "ok", note, expireAt: expireAfter };
};

// ----------------------------- ชำระเงิน -----------------------------

const APPLY_RANK = { plan: 0, addon: 1, points: 2, custom: 3 };

/** เรียงตามลำดับ apply: แพ็กเกจหลัก -> ฟีเจอร์เสริม -> point -> custom (คงลำดับเดิมในกลุ่มเดียวกัน) */
export const sortForApply = (lines) =>
  (lines || [])
    .map((line, index) => ({ line, index }))
    .sort((a, b) => APPLY_RANK[a.line.kind] - APPLY_RANK[b.line.kind] || a.index - b.index)
    .map((row) => row.line);

/** 1 บรรทัดตะกร้า = 1 บรรทัดใน QR (point ซื้อหลายชุด = quantity) */
export const qrItemsOf = (lines, orgId) =>
  sortForApply(lines).map((line) => ({
    productName: line.name,
    productCode: `cart_${line.id.replace(/[^a-z0-9_]+/gi, "_")}`,
    quantity: line.qty,
    price: line.unit,
    detail: { orgId, lineId: line.id, kind: line.kind, key: line.key || line.requestId || line.item.planKey || line.item.packKey || "", days: line.days || undefined },
  }));

/** idempotency key ต่อบรรทัด */
export const lineKey = (transaction, lineId) => `${transaction?.transactionId || ""}:${lineId}`;

/**
 * apply ตะกร้าหลังจ่ายสำเร็จ — เรียกซ้ำได้ (ปุ่มลองใหม่ของโมดัล) เพราะทุกบรรทัดส่ง idempotency key
 * deps = { activatePlan, activateAddon, topup, payCustom, refreshOrganizations }
 */
export const runCartPayment = async ({ lines, organization, transaction, by = "", deps }) => {
  let org = organization;
  for (const line of sortForApply(lines)) {
    const key = lineKey(transaction, line.id);

    if (line.kind === "plan") {
      const updated = await deps.activatePlan({
        organization: org, planKey: line.item.planKey, cycle: line.item.cycle, seats: line.seats, amount: line.amount, transaction, idempotencyKey: key,
      });
      if (updated) org = updated;
      await deps.refreshOrganizations?.();
    } else if (line.kind === "addon") {
      await deps.activateAddon({
        organization: org, kind: line.addonKind, key: line.key, blocks: line.blocks, renewId: line.renewId,
        name: line.name, amount: line.amount, days: line.days, transaction, by, idempotencyKey: key,
      });
    } else if (line.kind === "custom") {
      await deps.payCustom({
        orgId: org.id, id: line.requestId, amount: line.amount, transaction, by, idempotencyKey: key,
      });
    } else if (line.kind === "points") {
      for (let index = 0; index < line.qty; index += 1) {
        await deps.topup({
          orgId: org.id, packKey: line.item.packKey, amount: line.unit, transaction, by, idempotencyKey: `${key}:${index}`,
        });
      }
    }
  }
};
